// =============================================================================
// WidgetErrorBoundary — lokalny error boundary do owijania widgetow/chartow
// =============================================================================
// Crash w jednym widgetcie (np. Recharts AreaChart na uszkodzonym data) NIE
// kladzie calej aplikacji. Renderuje minimalny placeholder z nazwa widgetu
// i error msg + console.error dla debugu.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

interface Props {
  /** Nazwa widgetu (do logu i fallback) */
  name: string;
  children: ReactNode;
  /** Custom fallback. Default: maly amber kafelek z nazwa + error. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class WidgetErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[WidgetErrorBoundary:${this.props.name}]`, error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    return (
      <div className="border border-amber-500/30 bg-amber-500/5 rounded-md p-3 text-xs text-amber-700 dark:text-amber-400">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span className="font-medium">Widget "{this.props.name}" crashował</span>
        </div>
        <p className="text-[11px] text-muted-foreground font-mono">
          {this.state.error?.message ?? "Unknown error"}
        </p>
      </div>
    );
  }
}
